<template>
  <div class="avatar-selector-wrapper">
    <CustomPicker
        v-if="useCustomPicker"
        closeOnSelect
        inputLabel="انتخاب آواتار سطح"
        inputId="LevelAvatarPicker"
        :isLoading="isLoading"
        :multiple="false"
        :items="avatarOptions"
        v-model="selectedAvatar"
        @open="onOpen"
        @close="onClose"
        @search="handleSearch"
    >
      <template #no-options>هیچ آواتاری جهت نمایش وجود ندارد</template>
    </CustomPicker>

    <div v-else class="avatar-selector">
      <div
          class="avatar-selector__trigger"
          :class="{ 'avatar-selector__trigger--disabled': disabled }"
          @click="toggleDropdown"
          ref="trigger"
      >
        <div class="trigger-content">
          <div class="trigger-avatar" :class="currentMeta.colorClass">
            <img v-if="currentMeta.src" :src="currentMeta.src" width="40" height="40" alt="آواتار انتخاب شده" />
<!--            <div v-else class="trigger-avatar-placeholder">{{ currentLabel.charAt(0) }}</div>-->
          </div>
          <div class="trigger-info">
<!--            <div class="trigger-label">{{ currentLabel }}</div>-->
<!--            <div class="trigger-hint">آواتار سطح</div>-->
          </div>
        </div>
        <div class="trigger-arrow">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </div>

      <div v-if="isOpen" class="avatar-selector__dropdown" ref="dropdown">
        <div class="dropdown-content">
          <div class="dropdown-body">
            <div v-if="filteredOptions.length === 0" class="empty-state">
              <div class="empty-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M3 7V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7Z" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M3 9L10.3 13.15C10.7 13.3833 11.3 13.3833 11.7 13.15L21 9" stroke="currentColor" stroke-width="1.5"/>
                </svg>
              </div>
              <p class="empty-text">نتیجه‌ای یافت نشد</p>
            </div>

            <div v-else class="avatar-grid">
              <div
                  v-for="opt in filteredOptions"
                  :key="opt.key"
                  class="avatar-item"
                  :class="{ 'avatar-item--selected': String(opt.key) === String(value) }"
                  @click="select(opt.key)"
              >
                <div class="avatar-item__content">
                  <div class="avatar-circle" :class="opt.colorClass">
                    <img v-if="opt.src" :src="opt.src" width="36" height="36" alt="" />
<!--                    <div v-else class="avatar-fallback">{{ opt.label.charAt(0) }}</div>-->
                  </div>
<!--                  <div class="avatar-label">{{ opt.label }}</div>-->
                </div>
                <div v-if="String(opt.key) === String(value)" class="avatar-selected-indicator">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div v-if="clearable && value" class="dropdown-footer">
            <button class="clear-button" @click="select('')">
              <svg class="clear-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>حذف انتخاب</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { levelAvatarOptions, getLevelAvatarMeta } from "@core/utils/levelAvatars";

export default {
  name: "LevelAvatarSelect",
  props: {
    value: { type: String, default: "" },
    options: { type: Array, default: null },
    placeholder: { type: String, default: "انتخاب آواتار" },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true }
  },
  data() {
    return {
      q: "",
      isOpen: false,
      useCustomPicker: false,
      isLoading: false,
      selectedAvatar: null,
      searchVal: null,
      timeout: null
    };
  },
  computed: {
    safeOptions() {
      return Array.isArray(this.options) && this.options.length ? this.options : levelAvatarOptions;
    },
    filteredOptions() {
      const query = String(this.q || "").trim().toLowerCase();
      if (!query) return this.safeOptions;
      return this.safeOptions.filter((x) =>
          `${x.key} ${x.label}`.toLowerCase().includes(query)
      );
    },
    current() {
      return this.safeOptions.find((x) => String(x.key) === String(this.value)) || null;
    },
    currentLabel() {
      return this.current ? String(this.current.label || "") : this.placeholder;
    },
    currentMeta() {
      return getLevelAvatarMeta(this.value);
    },
    avatarOptions() {
      return this.safeOptions.map((opt) => ({
        ...opt,
        badge: opt.label,
        value: opt.key
      }));
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.selectedAvatar = this.safeOptions.find(opt =>
            String(opt.key) === String(val)
        ) || null;
      }
    },
    selectedAvatar: {
      handler(val) {
        if (this.useCustomPicker && val) {
          const avatarKey = val.key;
          this.$emit("input", avatarKey);
          this.$emit("change", avatarKey);
        }
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    toggleDropdown() {
      if (this.disabled) return;
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      }
    },
    select(key) {
      const selectedKey = String(key || "");
      this.$emit("input", selectedKey);
      this.$emit("change", selectedKey);
      this.q = "";
      this.isOpen = false;
    },
    handleClickOutside(event) {
      if (!this.isOpen) return;
      const trigger = this.$refs.trigger;
      const dropdown = this.$refs.dropdown;
      if (!trigger.contains(event.target) && !dropdown?.contains(event.target)) {
        this.isOpen = false;
      }
    },
    handleSearch(searchValue, loadingFn) {
      if (this.timeout) clearTimeout(this.timeout);
      this.timeout = setTimeout(async () => {
        this.searchVal = searchValue;
        loadingFn(true);
        loadingFn(false);
      }, 300);
    },
    onOpen() {},
    onClose() {}
  }
};
</script>

<style scoped>
.avatar-selector-wrapper {
  position: relative;
  width: 100%;
  max-width: 280px;
}

.avatar-selector {
  position: relative;
}

.avatar-selector__trigger {
  width: 100%;
  height: 64px;
  background: #ffffff;
  border: 1.5px solid #e5e7eb;
  border-radius: 14px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
}

.avatar-selector__trigger:hover:not(.avatar-selector__trigger--disabled) {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
}

.avatar-selector__trigger--disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f9fafb;
}

.trigger-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.trigger-avatar {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.trigger-avatar-placeholder {
  color: white;
  font-weight: 700;
  font-size: 18px;
}

.trigger-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: right;
}

.trigger-label {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
}

.trigger-hint {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.trigger-arrow {
  color: #9ca3af;
  transition: transform 0.2s ease;
}

.avatar-selector__trigger:hover .trigger-arrow {
  color: #3b82f6;
}

.avatar-selector__dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 1000;
  background: #ffffff;
  border: 1.5px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  animation: dropdownSlideIn 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

@keyframes dropdownSlideIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-content {
  display: flex;
  flex-direction: column;
  max-height: 400px;
}

.dropdown-header {
  padding: 16px;
  border-bottom: 1px solid #f3f4f6;
}

.search-wrapper {
  position: relative;
  width: 100%;
}

.search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

.search-input {
  width: 100%;
  height: 40px;
  padding: 0 36px 0 12px;
  background: #f9fafb;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  transition: all 0.2s ease;
  text-align: right;
}

.search-input:focus {
  outline: none;
  background: #ffffff;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-input::placeholder {
  color: #9ca3af;
}

.dropdown-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

.avatar-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.avatar-item {
  position: relative;
  border: 1.5px solid transparent;
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.avatar-item:hover {
  background: #f9fafb;
  border-color: #e5e7eb;
  transform: translateY(-1px);
}

.avatar-item--selected {
  background: rgba(34, 197, 94, 0.08);
  border-color: #22c55e;
}

.avatar-item__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.avatar-circle {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.avatar-fallback {
  color: white;
  font-weight: 700;
  font-size: 16px;
}

.avatar-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  text-align: center;
  line-height: 1.2;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.avatar-selected-indicator {
  position: absolute;
  top: 6px;
  left: 6px;
  width: 20px;
  height: 20px;
  background: #22c55e;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  animation: popIn 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes popIn {
  0% { transform: scale(0); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  gap: 8px;
}

.empty-icon {
  color: #d1d5db;
  opacity: 0.7;
}

.empty-text {
  font-size: 13px;
  font-weight: 600;
  color: #9ca3af;
  margin: 0;
}

.dropdown-footer {
  padding: 12px 16px;
  border-top: 1px solid #f3f4f6;
}

.clear-button {
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(239, 68, 68, 0.08);
  border: 1.5px solid rgba(239, 68, 68, 0.2);
  border-radius: 10px;
  color: #ef4444;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.clear-button:hover {
  background: rgba(239, 68, 68, 0.12);
  border-color: rgba(239, 68, 68, 0.3);
  transform: translateY(-1px);
}

.clear-icon {
  flex-shrink: 0;
}
</style>